import {
    Object3D,
    Color,
    SpriteMaterial,
    Sprite,
    Vector3,
    CanvasTexture,
} from 'three';

import { TweenMax } from 'gsap';

class Light extends Object3D {
    constructor(opts) {
        super();

        this.options = {
            camera: null,
            width: window.innerWidth,
            height: window.innerHeight,
            // wallColor: '#00091b',
            wallColor: 'rgba(0,0,0,0)',
            wallDistance: 400,
            lightColor: '#ffffff',
            noiseScale: 0.5,
            noiseTexture: '/img/texture_map_small.png',
            noiseTextureWidth: 256,
            noiseTextureHeight: 256,
            smallRadiusScale: 0.7,
            smallCircleOffset: 0.15,
            bigCircleOffset: 0.3,
            ...opts,
        };

        this.width = this.options.width;
        this.height = this.options.height;
        this.camera = this.options.camera;
        this.size = null;
        this.wall = null;
        this.intensity = 0;
        this.intensityTween = null;
        this.materialAlpha = 0;
        this.canvas = null;
        this.context = null;
        this.material = null;
        this.geometry = null;
        this.texture = null;
        this.noiseImage = document.createElement('img');
        this.noiseImage.src = this.options.noiseTexture;
    }

    buildCanvas() {
        this.canvas = document.createElement('canvas');
        this.context = this.canvas.getContext('2d');
    }

    build() {
        const {
            wallDistance,
        } = this.options;

        this.buildCanvas();

        this.size = this.getSizeAtDistance();

        // Wall
        this.texture = new CanvasTexture(this.canvas);
        this.material = new SpriteMaterial({
            map: this.texture,
            transparent: true,
        });
        this.wall = new Sprite(this.material);
        this.wall.position.z = this.camera.position.z - wallDistance;
        this.add(this.wall);

        this.updateMaterial();
    }

    updateMaterial() {
        const {
            wallColor,
            lightColor,
            noiseScale,
            noiseTextureWidth,
            noiseTextureHeight,
            smallRadiusScale,
            smallCircleOffset,
            bigCircleOffset,
        } = this.options;

        const maxRadius = Math.min(500, ((this.size.width * 1.5) / 2));
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        const x = this.canvas.width / 2;
        const radius = Math.round(maxRadius * 2);
        const radiusWithIntensity = radius * this.intensity;
        const smallOffset = (radiusWithIntensity * smallCircleOffset);
        const bigOffset = (radiusWithIntensity * bigCircleOffset);
        const y = (this.canvas.height / 2) + (this.size.height / 2) + smallOffset;
        const yBig = y + bigOffset;

        const gradient = this.context.createRadialGradient(x, yBig, radius, x, yBig, 0);
        gradient.addColorStop(1 - this.intensity, wallColor);
        gradient.addColorStop(1, lightColor);
        this.context.fillStyle = gradient;
        this.context.fillRect(x - radius, yBig - radius, radius * 2, radius * 2);

        const imageScale = noiseScale;
        const imageWidth = this.noiseImage.naturalWidth || noiseTextureWidth;
        const imageHeight = this.noiseImage.naturalHeight || noiseTextureHeight;
        const scaleWidth = Math.round(imageWidth * imageScale);
        const scaleHeight = Math.round(imageHeight * imageScale);
        const cols = Math.ceil(this.canvas.width / scaleWidth);
        const rows = Math.ceil(this.canvas.height / scaleHeight);
        for (let row = 0; row < rows; row += 1) {
            for (let col = 0; col < cols; col += 1) {
                this.context.drawImage(
                    this.noiseImage,
                    0,
                    0,
                    imageWidth,
                    imageWidth,
                    col * scaleWidth,
                    row * scaleHeight,
                    scaleWidth,
                    scaleHeight,
                );
            }
        }

        const overRadius = radius * smallRadiusScale;
        const overGradient = this.context.createRadialGradient(x, y, overRadius, x, y, 0);
        overGradient.addColorStop(1 - this.intensity, wallColor);
        overGradient.addColorStop(1 - (this.intensity * 0.1), lightColor);
        overGradient.addColorStop(1, lightColor);
        this.context.fillStyle = overGradient;
        this.context.fillRect(x - overRadius, y - overRadius, overRadius * 2, overRadius * 2);

        if (this.material !== null) {
            this.material.map.needsUpdate = true;
            // this.material.needsUpdate = true;
        }
    }

    getSizeAtDistance() {
        const { wallDistance } = this.options;
        const wallPosition = new Vector3(
            0,
            0,
            this.camera.position.z - wallDistance,
        );
        const distance = this.camera.position.distanceTo(wallPosition);
        const vFOV = this.camera.fov * (Math.PI / 180);
        const visibleHeight = 2 * Math.tan(vFOV / 2) * distance;
        const aspect = this.width / this.height;
        const visibleWidth = visibleHeight * aspect;

        const width2 = 2 ** Math.ceil(Math.log(visibleWidth) / Math.LN2);
        const height2 = 2 ** Math.ceil(Math.log(visibleHeight) / Math.LN2);

        return {
            width: visibleWidth,
            height: visibleHeight,
            width2,
            height2,
        };
    }

    setSize(width, height) {
        this.width = width;
        this.height = height;
        this.size = this.getSizeAtDistance();
        const wallSize = Math.max(this.size.width2, this.size.height2);
        this.wall.scale.set(wallSize, wallSize, 1);
        const canvasSize = Math.min(Math.max(this.size.width2, this.size.height2), 1024);
        this.canvas.width = canvasSize;
        this.canvas.height = canvasSize;
        this.updateMaterial();
    }

    setIntensity(intensity, duration) {
        if (intensity === this.intensity) {
            return;
        }
        this.intensity = intensity;
        this.updateMaterial();

        // if (this.intensityTween !== null) {
        //     this.intensityTween.kill();
        // }
        // this.intensityTween = TweenMax.to(this, duration || 0.2, {
        //     intensity,
        //     onUpdate: () => {
        //         this.updateMaterial();
        //     },
        // });
    }

    set alpha(alpha) {
        this.materialAlpha = alpha;
        if (alpha <= 0) {
            this.visible = false;
        } else if (!this.visible) {
            this.visible = true;
        }
        if (this.wall === null) {
            return;
        }
        this.wall.material.opacity = alpha;
    }

    get alpha() {
        return this.materialAlpha;
    }
}

export default Light;



// WEBPACK FOOTER //
// ./lib/LightCanvas.js